import {RouterProvider} from '@tanstack/react-router';
import {TanStackRouterDevtools} from '@tanstack/react-router-devtools';
import {type ReactNode, StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {I18nextProvider} from "react-i18next";
import {CasePartnerSidePanelContent} from "@/components/case-partner/CasePartnerSidePanelContent";
import {CaseProvider, useCaseContext} from "@/components/contexts/CaseContext";
import {CasePartnerProvider, useCasePartnerShell} from "@/components/contexts/CasePartnerContext";
import {NavigationProvider} from "@/components/contexts/NavigationContext";
import {SidePanelProvider} from "@/components/contexts/SidePanelProvider";
import {TimezoneProvider} from "@/components/contexts/TimezoneContext";
import {AppInstallPrompt} from "@/components/shared/AppInstallPrompt";
import {AppToaster} from "@/components/shared/AppToaster";
import {AppUpdateWatcher} from "@/components/shared/AppUpdateWatcher";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import {BackgroundSyncProvider} from "@/context/BackgroundSyncContext";
import {TagEditorProvider} from "@/context/TagEditorContext";
import {useAppInsights} from "@/hooks/useAppInsights";
import {useEngagedTime} from "@/hooks/useEngagedTime";
import {listenForInstallOffers} from "@/lib/appInstall";
import {initAppInsights} from "@/lib/applicationInsights";
import {AuthProvider} from "@/lib/auth/hooks/auth-provider";
import {EnvConfigBootstrap} from "@/lib/env-config";
import i18n from "@/lib/i18n";
import {router} from './router/routes';

// Side-effect CSS imports — Vite bundles these into the build.
import '../styles/globals.css';
import '../styles/citations.css';
import '../styles/pdf-highlights.css';
import 'react-quill-new/dist/quill.snow.css';
import 'superdoc/style.css';
import '../styles/quill-custom.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found in index.html');

listenForInstallOffers();

const AppInsightsTracker = () => {
  useAppInsights();
  // After useAppInsights so a session's final sample flushes before the auth effect clears the user.
  useEngagedTime();
  return null;
}

const CasePartnerSidePanelProvider = ({children}: {children: ReactNode}) => {
  const casePartnerShell = useCasePartnerShell();
  const {selectedCase} = useCaseContext();

  return (
    <SidePanelProvider
      casePartnerContent={<CasePartnerSidePanelContent />}
      shouldKeepCasePartnerTab={Boolean(selectedCase) && !casePartnerShell.isFullPageOpen}
    >
      {children}
    </SidePanelProvider>
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <EnvConfigBootstrap onLoaded={initAppInsights}>
          <AuthProvider>
            <AppInsightsTracker/>
            <AppUpdateWatcher/>
            <AppInstallPrompt/>
            <BackgroundSyncProvider>
              <NavigationProvider>
                <TimezoneProvider>
                  <CaseProvider>
                    <CasePartnerProvider>
                      <CasePartnerSidePanelProvider>
                        <TagEditorProvider>
                          <AppToaster/>
                          <RouterProvider router={router}/>
                          {import.meta.env.DEV && <TanStackRouterDevtools router={router} position='bottom-right' />}
                        </TagEditorProvider>
                      </CasePartnerSidePanelProvider>
                    </CasePartnerProvider>
                  </CaseProvider>
                </TimezoneProvider>
              </NavigationProvider>
            </BackgroundSyncProvider>
          </AuthProvider>
        </EnvConfigBootstrap>
      </I18nextProvider>
    </ErrorBoundary>
  </StrictMode>
);
